<template>
  <div class="container relative flex flex-col w-full h-full overflow-hidden">
    <!-- HEADER -->
    <tx-form-header class="flex flex-col mx-2 my-[30px] grow-0 shrink-0 justify-center header" :title="t('articleDetails.actions.editSegmentations')" :show-header="showHeader" />

    <!-- BODY -->
    <div class="px-10 mt-4 alerts">
      <tx-alert :show="hasError" type="error" :text="errorMessage" dismissible />
    </div>
    <!-- FORM -->
    <div class="overflow-hidden body h-full flex flex-col">
      <div class=" h-full w-full mt-2 overflow-hidden flex flex-col">
        <div class="w-full px-10 mb-5">
          <tx-input v-model="filter" :label="t('general.filter')" :placeholder="t('general.filter')" clearable @change="onFilterChange" />
        </div>
        <div class="w-full px-10 my-2 mb-6">
          <tx-checkbox
            v-model="checkAll" :label="t('general.checkAll')" :indeterminate="isIndeterminate"
            @change="handleCheckAllChange"
          />
        </div>
        <div v-if="userStore.activeCatalog && userStore.activeCatalog.Config.SegmentationGrouping && userStore.activeCatalog.Config.SegmentationGrouping.levels > 1" :class="layoutClass" class="w-full px-10 overflow-auto">
          <div v-for="group in segmentationTree" :key="group.label" class="w-full">
            <div class="text-lg font-semibold mb-2">
              {{ group.label }}
            </div>
            <div v-if="group.subGroups && group.subGroups.length > 0" class="flex flex-col gap-4 pl-2">
              <div v-for="subGroup in group.subGroups" :key="subGroup.label" class="flex flex-col gap-2">
                <div class="text-base font-semibold">
                  {{ subGroup.label }}
                </div>
                <div v-for="segmentation in subGroup.segmentations" :key="segmentation.segmentation.Id" class="flex pl-2">
                  <label class="flex items-center w-40">
                    <input
                      v-model="checkedSegmentations" type="checkbox" :value="segmentation.segmentation.Id"
                      :indeterminate="indeterminateSegmentationIds.has(segmentation.segmentation.Id)"
                      @change="handleCheckedSegmentationChange(segmentation.segmentation.Id)"
                    >
                    <span class="pl-1 text-base" v-text="segmentation.label" />
                  </label>
                  <form-editor
                    v-if="userStore.activeCatalog?.SegmentationPlanningEnable && checkedSegmentations.indexOf(segmentation.segmentation.Id) >= 0"
                    v-model="planningValues[segmentation.segmentation.Id]"
                    :attribute="planningValueField"
                    :form="planningValues"
                    :required="true"
                    :show-label="false"
                    :clearable="false"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div v-else class="w-full px-10 overflow-auto">
          <div v-for="segmentation in filteredAvailableSegmentations" :key="segmentation.Id" :class="subLayoutClass">
            <label class="flex items-center">
              <input
                v-model="checkedSegmentations" type="checkbox" :value="segmentation.Id"
                :indeterminate="indeterminateSegmentationIds.has(segmentation.Id)"
                @change="handleCheckedSegmentationChange(segmentation.Id)"
              >
              <span class="pl-1 text-base" v-text="segmentation.Name" />
            </label>
            <form-editor
              v-if="userStore.activeCatalog?.SegmentationPlanningEnable && checkedSegmentations.indexOf(segmentation.Id) >= 0"
              v-model="planningValues[segmentation.Id]"
              :attribute="planningValueField"
              :form="planningValues"
              :required="true"
              :show-label="false"
              :clearable="false"
            />
          </div>
        </div>
      </div>

      <!-- FOOTER -->
      <tx-form-footer
        class="flex flex-row justify-end flex-shrink-0 flex-nowrap"
        :primary-text="t(('general.updateCount'), { count: checkedSegmentations.length })" :primary-loading="loading" :secondary-disabled="loading"
        @primary-click="onUpdate" @secondary-click="onCancel"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { computed, onMounted, ref, watch } from 'vue'
import TxCheckbox from '@/shared/components/TxCheckbox.vue'
import TxFormFooter from '@/shared/components/forms/TxFormFooter.vue'
import TxAlert from '@/shared/components/TxAlert.vue'
import type MyArticle from '@/models/myArticle'
import type { UpdateArticleSegmentationsModel, UpdateArticlesSegmentationsBulkItem, UpdateArticlesSegmentationsBulkItemSeg } from '@/api/t1/model/articleModel'
import { updateArticleSegmentations, updateArticlesSegmentationsBulk } from '@/api/t1/article'
import { appConstants, backgroundJobConstants } from '@/models/constants'
import useErrorMessage from '@/shared/composables/errorMessage'
import { useUserStore } from '@/store/userData'
import TxFormHeader from '@/shared/components/forms/TxFormHeader.vue'
import FormEditor from '@/shared/components/FormEditor.vue'
import { AttributeType } from '@/models/catalogAttribute'
import type Article from '@/models/article'
import TxInput from '@/shared/components/TxInput.vue'
import type CatalogSegmentation from '@/models/catalogSegmentation'
import appConfig from '@/services/appConfig'
import utils from '@/services/utils'
import Job from '@/models/job'
import { useNotificationStore } from '@/store/notification'

interface IProps {
  articles: MyArticle[]
  showHeader?: boolean
  isModel?: boolean
}
const props = withDefaults(defineProps<IProps>(), {
  showHeader: true,
  isModel: false,
})

const emit = defineEmits<{
  (e: 'cancel'): void
  (e: 'updated', articles: MyArticle[] | Article[]): void
}>()

const { t } = useI18n()
const userStore = useUserStore()
const { errorMessage, hasError } = useErrorMessage()
const notificationStore = useNotificationStore()

const loading = ref(false)
const checkAll = ref(false)
const isIndeterminate = ref(false)
const filter = ref('')

const planningValues = ref<Record<number, string>>({})
const checkedSegmentations = ref<number[]>([])
const indeterminateSegmentationIds = ref(new Set<number>())
const articles = ref<Article[]>([])
const defaultPlanningValues = [t('editSegmentations.assorted'), t('editSegmentations.planned')]

const availablePlanningValues = computed(() => {
  const configuredPlanningValue = userStore.activeCatalog?.Config.SegmentationPlanningValues
  if (userStore.activeCatalog!.SegmentationPlanningEnable && utils.isDefined(configuredPlanningValue) && Object.keys(configuredPlanningValue).length) {
    const keys = Object.keys(configuredPlanningValue)
    const values = ['assorted', 'planned'].map((targetKey, index) => {
      const matchedKey = keys.find(k => k.toLowerCase() === targetKey.toLowerCase())

      return matchedKey && configuredPlanningValue![matchedKey] ? configuredPlanningValue[matchedKey] : defaultPlanningValues[index]
    })
    return values
  }
  else {
    return defaultPlanningValues
  }
})

const availableSegmentations = computed(() => {
  if (userStore.activeCatalog) {
    return Object.values(userStore.activeCatalog._IndexedCatalogSegmentation)
      .filter(x => x.Status > 0 && !appConstants.excludeSegmentationNames.has(x.Name.toLowerCase()) && x.Editable)
      .sort((a, b) => a.Name.localeCompare(b.Name))
  }
  return []
})
const filteredAvailableSegmentations = computed(() => {
  return availableSegmentations.value.filter((seg) => {
    if (seg.Name.toLowerCase().includes(filter.value.toLowerCase())) {
      return true
    }
    else {
      return false
    }
  })
})
const segmentationTree = computed(() => {
  const tree: { label: string, subGroups?: { label: string, segmentations: { label: string, segmentation: CatalogSegmentation }[] }[], segmentations: { label: string, segmentation: CatalogSegmentation }[] }[] = []
  if (userStore.activeCatalog && userStore.activeCatalog.Config.SegmentationGrouping && userStore.activeCatalog.Config.SegmentationGrouping.levels > 1) {
    filteredAvailableSegmentations.value.forEach((segmentation) => {
      if (segmentation.Name.includes('-')) {
        const splits = segmentation.Name.split('-')
        if (splits.length > 1) {
          const groupName = splits[0].trim()
          let groupIndex = tree.findIndex(g => g.label === groupName)

          if (groupIndex === -1) {
            groupIndex = tree.length
            tree.push({ label: groupName, subGroups: [], segmentations: [] })
          }

          if (splits.length > 2) {
            const subGroupName = splits[1].trim()
            let subGroupIndex = tree[groupIndex].subGroups?.findIndex(sg => sg.label.toLowerCase() === subGroupName.toLowerCase())
            if (subGroupIndex === undefined || subGroupIndex === -1) {
              subGroupIndex = tree[groupIndex].subGroups?.length || 0
              tree[groupIndex].subGroups!.push({ label: subGroupName, segmentations: [] })
            }
            tree[groupIndex].subGroups![subGroupIndex].segmentations.push({
              label: splits[2].trim(),
              segmentation,
            })
          }
          else {
            tree[groupIndex].segmentations.push({
              label: splits[1].trim(),
              segmentation,
            })
          }
        }
      }

      if (!planningValues.value[segmentation.Id]) {
        planningValues.value[segmentation.Id] = availablePlanningValues.value[0]
      }
    })
  }
  return tree
})

const layoutClass = computed(() => {
  const segmentationsName = availableSegmentations.value.map(seg => seg.Name)
  const hasLongText = segmentationsName.some(t => t.length > 30)
  return hasLongText
    ? 'flex flex-row flex-wrap gap-8'
    : 'grid grid-cols-2  gap-4'
})

const subLayoutClass = computed(() => {
  const segmentationsName = availableSegmentations.value.map(seg => seg.Name)
  const hasLongText = segmentationsName.some(t => t.length > 30)
  return hasLongText
    ? 'items-center w-full'
    : 'grid items-center w-full grid-cols-2 h-14'
})

function handleCheckAllChange(val: TxBooleanish) {
  const filteredIds = filteredAvailableSegmentations.value.map(s => s.Id)

  if (val) {
    checkedSegmentations.value = Array.from(new Set([
      ...checkedSegmentations.value,
      ...filteredIds,
    ]))
    // Remove all filtered IDs from indeterminate list
    filteredIds.forEach(id => indeterminateSegmentationIds.value.delete(id))
  }
  else {
    checkedSegmentations.value = checkedSegmentations.value.filter(id => !filteredIds.includes(id))
    // Add filtered IDs to indeterminate list (if previously applied to articles)
    filteredIds.forEach(id => indeterminateSegmentationIds.value.delete(id))
  }

  const checkedCount = checkedSegmentations.value.filter(id => filteredIds.includes(id)).length
  checkAll.value = checkedCount === filteredIds.length
  isIndeterminate.value = checkedCount > 0 && checkedCount < filteredIds.length
}

function onFilterChange() {
  const filteredIds = filteredAvailableSegmentations.value.map(s => s.Id)
  const checkedFiltered = checkedSegmentations.value.filter(id => filteredIds.includes(id))

  checkAll.value = filteredIds.length > 0 && checkedFiltered.length === filteredIds.length
  isIndeterminate.value = filteredIds.length > 1 && checkedFiltered.length > 0 && checkedFiltered.length < filteredIds.length
}

const planningValueField: IFormEditorAttribute = Object.assign({}, appConstants.staticFieldTemplate, {
  SystemName: 'PlanningValue',
  DisplayName: '',
  Creatable: true,
  AttributeType: AttributeType.Nvarchar,
  ReadOnly: false,
  IsRequired: true,
  VettingList: availablePlanningValues.value,
})

function handleCheckedSegmentationChange(segmentationId: number) {
  const filteredIds = filteredAvailableSegmentations.value.map(s => s.Id)
  const checkedFiltered = checkedSegmentations.value.filter(id => filteredIds.includes(id))

  checkAll.value = checkedFiltered.length === filteredIds.length
  isIndeterminate.value = filteredIds.length > 1 && checkedFiltered.length > 0 && checkedFiltered.length < filteredIds.length

  // if (checkedSegmentations.value.includes(segmentationId)) {
  indeterminateSegmentationIds.value.delete(segmentationId)
  // }
  // else {
  //   // If this segmentation was originally present in any article, mark as indeterminate
  //   if (articles.value.some(article => article._Segmentations[segmentationId])) {
  //     indeterminateSegmentationIds.value.add(segmentationId)
  //   }
  // }
}

function onCancel() {
  emit('cancel')
}

async function onUpdate() {
  if (userStore.activeCatalog && props.articles.length > 0) {
    loading.value = true
    const catalogCode = userStore.activeCatalog.CatalogCode
    const promises: Promise<any>[] = []
    const updatedArticles: (Article | MyArticle)[] = []
    const failedArticles: string[] = []

    if (articles.value.length <= userStore.activeCatalog.Config.T1BackgroundJobThreshold.updateArticleSegmentations) {
      articles.value.forEach((article) => {
        const requestObj: UpdateArticleSegmentationsModel[] = []
        Object.keys(article._Segmentations).forEach((segId) => {
          if (userStore.activeCatalog?._IndexedCatalogSegmentation[segId]
            && appConstants.excludeSegmentationNames.has(userStore.activeCatalog?._IndexedCatalogSegmentation[segId].Name.toLowerCase())) {
            requestObj.push({ SegmentationId: Number(segId), PlanningValue: article._Segmentations[segId].PlanningValue })
          }
          else if (indeterminateSegmentationIds.value.has(Number(segId))) {
            requestObj.push({ SegmentationId: Number(segId), PlanningValue: article._Segmentations[segId].PlanningValue })
          }
        })
        checkedSegmentations.value.forEach((segmentationId) => {
          requestObj.push({
            SegmentationId: segmentationId,
            PlanningValue: userStore.activeCatalog!.SegmentationPlanningEnable && userStore.activeCatalog?.Config.SegmentationPlanningValues && Object.keys(userStore.activeCatalog?.Config.SegmentationPlanningValues).length ? availablePlanningValues.value.indexOf(planningValues.value[segmentationId]) === 0 ? defaultPlanningValues[0] : defaultPlanningValues[1] : planningValues.value[segmentationId],
          })
        })
        promises.push(updateArticleSegmentations(catalogCode, article.Id, requestObj)
          .then((response) => {
            if (response.data) {
              // const filteredData = response.data.filter(data => data.Status === 1)
              // article._Segmentations = filteredData.map(itm => new Segmentation({ Id: itm.SegmentationId, PlanningValue: itm.PlanningValue }))
              updatedArticles.push(article)
            }
          })
          .catch(() => failedArticles.push(article.ArticleNumber)),
        )
      })
      Promise.all(promises)
        .then(async () => {
          if (updatedArticles.length) {
            emit('updated', updatedArticles)
          }
          if (failedArticles.length) {
            errorMessage.value = t('editSegmentations.failedUpdateSegmentations', { articles: failedArticles.join(', ') })
          }
        })
        .catch((e) => {
          console.error(e)
          errorMessage.value = t('general.unexpectedError')
        })
        .finally(() => loading.value = false)
    }
    else {
      const payload: UpdateArticlesSegmentationsBulkItem[] = []
      articles.value.forEach((article) => {
        const segPayload: UpdateArticlesSegmentationsBulkItemSeg[] = []
        checkedSegmentations.value.forEach((segmentationId) => {
          if (userStore.activeCatalog?._IndexedCatalogSegmentation[segmentationId]) {
            const segName = userStore.activeCatalog._IndexedCatalogSegmentation[segmentationId].Name
            segPayload.push({
              SID: segName,
              S: 1,
              PlanningValue: userStore.activeCatalog!.SegmentationPlanningEnable && userStore.activeCatalog?.Config.SegmentationPlanningValues && Object.keys(userStore.activeCatalog?.Config.SegmentationPlanningValues).length ? availablePlanningValues.value.indexOf(planningValues.value[segmentationId]) === 0 ? defaultPlanningValues[0] : defaultPlanningValues[1] : planningValues.value[segmentationId],
            })
          }
        })
        payload.push({ AN: article.ArticleNumber, S: segPayload })
      })
      updateArticlesSegmentationsBulk(catalogCode, payload)
        .then(async (res) => {
          const job = new Job({
            Id: res.data.Id.toString(),
            CatalogCode: userStore.activeCatalog!.CatalogCode,
            Type: backgroundJobConstants.t1JobTypes.editSegmentations,
            Status: res.data.Status,
            UserId: userStore.userProfile.Id,
            CreatedDate: new Date(),
          })
          if (appConfig.DB) {
            await appConfig.DB.jobs.put(job)
          }
          notificationStore.addNotification({ message: t('editSegmentations.jobSuccess'), type: 'Success' })
          onCancel() // close
        })
        .catch((e) => {
          console.error(e)
          errorMessage.value = t('general.unexpectedError')
        })
        .finally(() => loading.value = false)
    }
  }
}

async function reset() {
  loading.value = false
  errorMessage.value = ''
  checkAll.value = false
  isIndeterminate.value = false
  planningValues.value = {}
  checkedSegmentations.value = []
  indeterminateSegmentationIds.value.clear()

  if (props.isModel && appConfig.DB && userStore.activeCatalog) {
    const modelIds = [...new Set(props.articles?.map(obj => obj.ModelId))]
    const modelArticles = await appConfig.DB.getArticlesByCatalogCodeModelIds(userStore.activeCatalog, userStore.linkedCatalogDetails, modelIds, userStore.currentCustomer, [], false)
    if (modelArticles?.length) {
      modelArticles.forEach(article => articles.value.push(article))
    }
  }
  else {
    articles.value = props.articles
  }

  availableSegmentations.value.forEach((segmentation) => {
    let planningValue: string | null = null
    let allSame = true
    let presentCount = 0

    articles.value.forEach((article) => {
      const seg = article._Segmentations[segmentation.Id]
      if (seg) {
        presentCount++
        const currentValue = seg.PlanningValue?.toLowerCase() === 'planned' ? availablePlanningValues.value[1] : availablePlanningValues.value[0]
        if (planningValue === null) {
          planningValue = currentValue
        }
        else if (planningValue !== currentValue) {
          allSame = false
        }
      }
    })

    const total = articles.value.length
    if (presentCount === 0) {
      return
    }

    if (presentCount === total && allSame && planningValue !== null) {
      planningValues.value[segmentation.Id] = planningValue
      checkedSegmentations.value.push(segmentation.Id)
      indeterminateSegmentationIds.value.delete(segmentation.Id)
    }
    else {
      planningValues.value[segmentation.Id] = ''
      indeterminateSegmentationIds.value.add(segmentation.Id)
    }
  })
  // Compute initial checkAll and isIndeterminate state
  const filteredIds = filteredAvailableSegmentations.value.map(s => s.Id)
  const checkedFiltered = checkedSegmentations.value.filter(id => filteredIds.includes(id))

  checkAll.value = filteredIds.length > 0 && checkedFiltered.length === filteredIds.length
  isIndeterminate.value = filteredIds.length > 1 && checkedFiltered.length > 0 && checkedFiltered.length < filteredIds.length
}

watch(() => props.articles, reset)

onMounted(() => reset())
</script>
